import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import Button from "web/react/components/LEGACY_button/button";
import LegalAgreement from "web/react/components/legal-agreement/legal-agreement";
import PolicyText from "web/react/components/policy-text/policy-text";
import SignInLink from "web/react/components/sign-in-link/sign-in-link";
import SocialButton from "web/react/components/social-button/social-button";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { SSO_PROVIDERS } from "web/script/utils/social-auth-login";
import styles from "./email-capture-form.module.css";

export function EmailCaptureForm({
    captureType,
    className,
    emailAddress,
    emailLabelText,
    formErrors,
    onEmailChange,
    onLegalChange,
    onSubmit,
    privacyPolicyLink,
    submitText,
    submitClass,
    termsAndConditionsLink,
    cookiePolicyLink,
    shortPolicy,
    showSocial,
    hideEmail = false,
    onSocialClick,
    isFBPDP,
    signInLink,
    next,
    searchTerm,
    searchGender,
    type,
    productId,
    designerId,
    autoFocus = true,
}) {
    const country = environment.get("country");
    const requiresOptIn = country !== "US";

    function generateErrorDiv(message, key) {
        return (
            <div key={key} className={styles.formItemError}>
                <div dangerouslySetInnerHTML={{ __html: message }} />
            </div>
        );
    }

    let errors = {
        email: undefined,
        legal: undefined,
        other: undefined,
    };
    if (formErrors !== undefined) {
        Object.keys(formErrors).map((key) => {
            if (key === "marketing_emails" || key === "privacy_policy") {
                analytics.event(captureType, "sign up", "error_marketing");
                errors.legal = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else if (key === "email") {
                analytics.event(captureType, "sign up", "error_invalid_email");
                errors.email = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else if (key === "existing_user") {
                analytics.event(captureType, "sign up", "error_existing_user");
                errors.email = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else {
                errors.other = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            }
        });
    }

    const email = (
        <fieldset className={styles.fieldset}>
            {!isFBPDP && (
                <label className={styles.label} htmlFor="id_email">
                    {emailLabelText || gettext("account.form.email_label")}
                </label>
            )}
            <input
                type="email"
                name="email"
                value={emailAddress}
                onClick={() => analytics.event(captureType, "fill_field", "field_email")}
                className={styles.input}
                placeholder={gettext("account.form.email_placeholder")}
                autoCapitalize="off"
                onChange={onEmailChange}
                autoFocus={autoFocus}
                required
            />
            {errors.email}
        </fieldset>
    );

    const hasEmailAndSocial = !hideEmail && showSocial;

    const social = (
        <div className={styles.socialArea}>
            {hasEmailAndSocial && (
                <span className={styles.orSeparator}>
                    {gettext("in_stock_product.or_separator")}
                </span>
            )}
            <SocialButton
                className={styles.facebookButton}
                type="facebook"
                onClick={() => onSocialClick(SSO_PROVIDERS.facebook)}
                fullWidth
            >
                {gettext("account.register.sign_up_with_facebook")}
            </SocialButton>
            <SocialButton
                type="google"
                onClick={() => onSocialClick(SSO_PROVIDERS.google)}
                fullWidth
            >
                {gettext("account.register.sign_up_with_google")}
            </SocialButton>
        </div>
    );

    const legal = (
        <div className={styles.legalAgreementWrapper}>
            <LegalAgreement
                requiresOptIn={requiresOptIn}
                submitText={submitText}
                handleLegalChange={onLegalChange}
                captureType={captureType}
                privacyPolicyLink={privacyPolicyLink}
                termsAndConditionsLink={termsAndConditionsLink}
                cookiePolicyLink={cookiePolicyLink}
                shortVersion={shortPolicy}
            />
            {errors.legal}
        </div>
    );

    const buttons = (
        <React.Fragment>
            {requiresOptIn && legal}
            {!hideEmail && (
                <Button
                    styleType={"cta"}
                    className={clsx(submitClass, styles.submitButton)}
                    type="submit"
                    fullWidth
                >
                    {submitText}
                </Button>
            )}
            {errors.other}
            {showSocial === true && social}
            {!requiresOptIn && legal}
        </React.Fragment>
    );

    const showSignInLink = !!signInLink;

    return (
        <form className={clsx(className, styles.form)} onSubmit={onSubmit}>
            {!hideEmail && email}
            {showSignInLink && (
                <SignInLink
                    className={styles.signinBox}
                    emailAddress={emailAddress}
                    signInLink={signInLink}
                    next={next}
                    searchTerm={searchTerm}
                    searchGender={searchGender}
                    type={type}
                    productId={productId}
                    designerId={designerId?.toString()}
                    area={"heart"}
                    emailCapture
                />
            )}
            {buttons}
            {requiresOptIn && shortPolicy && <PolicyText className={styles.legalAgreementSmall} />}
        </form>
    );
}

export default EmailCaptureForm;

EmailCaptureForm.propTypes = {
    captureType: PropTypes.string.isRequired,
    className: PropTypes.string,
    cookiePolicyLink: PropTypes.string,
    emailAddress: PropTypes.string,
    emailLabelText: PropTypes.string,
    formErrors: PropTypes.object,
    onEmailChange: PropTypes.func.isRequired,
    onLegalChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSocialClick: PropTypes.func,
    showSocial: PropTypes.bool,
    hideEmail: PropTypes.bool,
    privacyPolicyLink: PropTypes.string.isRequired,
    submitText: PropTypes.string.isRequired,
    submitClass: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortPolicy: PropTypes.bool,
    isFBPDP: PropTypes.bool,
    signInLink: PropTypes.string,
    next: PropTypes.string,
    searchTerm: PropTypes.string,
    searchGender: PropTypes.string,
    type: PropTypes.string,
    productId: PropTypes.string,
    designerId: PropTypes.number,
    autoFocus: PropTypes.bool,
};
